import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { RefreshCw, Loader2, PenLine, ExternalLink, PackageOpen } from "lucide-react";

export default function Gallery() {
  const { activeId, activeClient } = useClient();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    if (!activeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { client_id: activeId } });
      setProducts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const rescrape = async () => {
    if (!activeId) return;
    setScraping(true);
    try {
      const { data } = await api.post(`/clients/${activeId}/scrape`);
      setProducts(data);
      toast.success(`${data.length} produits importés`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setScraping(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  return (
    <>
      <PageHeader
        overline={`Import automatique · ${activeClient?.website_url || "—"}`}
        title="Galerie Produits"
      >
        <button
          data-testid="rescrape-button"
          onClick={rescrape}
          disabled={scraping || !activeId}
          className="flex items-center gap-2 border border-white/15 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
        >
          {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Réimporter
        </button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-sm p-12 text-center">
            <PackageOpen className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              Aucun produit importé pour cette marque. Lance l'import depuis le site.
            </p>
            <button
              onClick={rescrape}
              disabled={scraping || !activeId}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors disabled:opacity-50"
            >
              {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Importer les produits
            </button>
          </div>
        ) : (
          <div
            data-testid="product-grid"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-t border-l border-white/10"
          >
            {products.map((p, i) => (
              <div
                key={p.id}
                className="group border-r border-b border-white/10 bg-secondary animate-fade-up"
                style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
                data-testid={`product-card-${i}`}
              >
                <div className="aspect-square bg-[#0d0d0d] overflow-hidden relative">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-2">
                    <button
                      data-testid={`use-product-${i}`}
                      onClick={() => navigate("/composer", { state: { product: p } })}
                      className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors"
                    >
                      <PenLine className="h-3.5 w-3.5" />
                      Créer un post
                    </button>
                  </div>
                </div>
                <div className="p-4 border-t border-white/10">
                  <p className="text-sm font-medium leading-snug line-clamp-2 min-h-[2.5rem]">
                    {p.title}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-xs text-accent font-medium">
                      {p.price || "—"}
                    </span>
                    {p.product_url && (
                      <a
                        href={p.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
